import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import {
  Container,
  CardContainer,
  Content,
  PoweredBy,
  Card,
} from '../../v2/styles'
import { useParams } from 'react-router-dom'
import { ConditionalCardWrapper } from '../order-details/utils'
import Logo from '../order-details/logo'
import Header from '../order-details/header/header'
import Spinner from '../order-details/loader'
import NotFoundCard from '../not-found/not-found-card'
import CourierInfo from '../order-details/courier-info'
import RobotInfo from '../order-details/robot-info'
import RobotProof from '../order-details/robot-proof'
import RobotAccessCode from '../order-details/robot-access-code'
import ShipmentInfo from '../order-details/shipment-info'
import QuiqupLogo from '../../assets/quiqup-logo.png'
import DestinationInfo from '../order-details/destination-info'
import CanLeaveAtDoor from '../order-details/can-leave-at-door'
import PaymentInfo from '../order-details/payment-info'
import OtpInfo from '../order-details/otp-info'
import CustomerServiceInfo from '../order-details/customer-service-info'
import OriginInfo from '../order-details/origin-info'
import AuthenticateForm from '../order-details/authenticate-form'
import ReturnRequestCard from '../order-details/return-request-card'
import ReturnRequestForm from '../order-details/return-request-form'
import { useTracking } from '../../hooks/useTracking'
import { useGetShipment } from '../../hooks/useGetShipment'

type ViewState = 'order-detail' | 'return-form'

export default function OrderDetailsPage() {
  const { uuid }: { uuid?: string } = useParams()
  const {
    orderDetails,
    liveOrder,
    hasMap,
    hasWidget,
    isLoadingOrder,
    isOrderError,
    getOrder,
  } = useTracking()
  // The layout is the same whether the slot holds the courier map or Nabd's
  // robot widget (sc-29236); the robot card stands in for the courier card.
  const mapSlotFilled = hasMap || hasWidget
  const bottomCardExists = Boolean(liveOrder.quiqee?.name) || hasWidget

  const { data: shipmentData, isLoading: isLoadingShipment } = useGetShipment()

  const [viewState, setViewState] = useState<ViewState>('order-detail')
  // Tracks whether user has visited the form view. When returning from the form,
  // we skip card entrance animations so they appear instantly.
  const hasVisitedFormRef = useRef(false)
  // Ephemeral: lost on page refresh. After refresh, ReturnStatusDisplay shows a
  // generic "return requested" message without polling. The eligibility API doesn't
  // return the existing request ID, so there's no recovery path for now.
  const [returnRequestId, setReturnRequestId] = useState<string | undefined>()

  useEffect(() => {
    if (uuid) {
      getOrder({ orderId: uuid, replace: true, triggerAlert: false })
    }
  }, [getOrder, uuid])

  if (
    (isLoadingOrder && !orderDetails) ||
    (!orderDetails && !isOrderError) ||
    isLoadingShipment
  ) {
    return <Spinner />
  }

  if (isOrderError && !orderDetails) {
    return (
      <NotFoundCard
        data-testid="invalid-order"
        title="We can't seem to find this shipment in our system."
        subTitle="Please contact your shipper."
      />
    )
  }

  return (
    <Container data-testid="order-info">
      <ConditionalCardWrapper>
        <CardContainer mapExist={mapSlotFilled}>
          <Card mapExist={mapSlotFilled}>
            {viewState === 'order-detail' && (
              <>
                <Logo />
                <Header countryCode={shipmentData?.origin_country || 'AE'} />
              </>
            )}
            <Content
              mapExist={viewState === 'order-detail' ? mapSlotFilled : false}
              courierExist={
                viewState === 'order-detail' ? bottomCardExists : false
              }
            >
              {viewState === 'order-detail' ? (
                <AnimatePresence mode="popLayout" initial={!hasVisitedFormRef.current}>
                  <AuthenticateForm
                    key="authentication-form"
                    shipmentData={shipmentData}
                  />
                  <CourierInfo key="courier-info" />
                  <RobotInfo key="robot-info" />
                  {/* Takes the courier card's slot once the leg is over:
                      RobotInfo is gone by then (no widget on a terminal
                      order) and there is no courier proof to show (sc-29235). */}
                  <RobotProof key="robot-proof" />
                  <ShipmentInfo key="shipment-info" data={shipmentData} />
                  <OriginInfo key="origin-info" />
                  <DestinationInfo key="destination-info" />
                  <CanLeaveAtDoor key="can-leave-at-door" />
                  <PaymentInfo
                    key="payment-info"
                    countryCode={shipmentData?.origin_country || 'AE'}
                  />
                  <OtpInfo key="otp-info" />
                  <RobotAccessCode key="robot-access-code" />
                  <CustomerServiceInfo
                    key="customer-service-info"
                    countryCode={shipmentData?.origin_country || 'AE'}
                  />
                  <ReturnRequestCard
                    key="return-request"
                    onRequestReturn={() => {
                      hasVisitedFormRef.current = true
                      setViewState('return-form')
                    }}
                    returnRequestId={returnRequestId}
                  />
                </AnimatePresence>
              ) : (
                <ReturnRequestForm
                  onBack={() => setViewState('order-detail')}
                  onSuccess={(id) => {
                    setReturnRequestId(id)
                    setViewState('order-detail')
                  }}
                />
              )}
            </Content>
            <PoweredBy href="https://www.quiqup.com/" data-testid="site-link">
              <div className="android-bottom-fix"></div>
              Powered by <img src={QuiqupLogo} height={30} alt="Quiqup" />
            </PoweredBy>
          </Card>
        </CardContainer>
      </ConditionalCardWrapper>
    </Container>
  )
}
